import React from "react";
import * as Popover from "@radix-ui/react-popover";
import {
  ColorSelect,
  ColorSelectContent,
  ColorSelectItem,
  ColorSelectTrigger,
} from "@/components/ui/color-select";
import { COLOR_PALETTE } from "@/common/colorUtils";
import type { HandleLegendItemChangeArguments } from "@/types/report";
import { SeriesConfig, SeriesConfigItem } from "@/common/type/report-types";

interface LegendV2Props {
  items: SeriesConfig;
  onItemChange: (args: HandleLegendItemChangeArguments) => void;
  direction?: "horizontal" | "vertical";
  limit?: number;
}

const LegendV2: React.FC<LegendV2Props> = ({
  items,
  onItemChange,
  direction = "vertical",
  limit = 3,
}) => {
  const itemEntries = Array.from(items.entries());

  // In the case of single-measure charts, the measure key will be the first part
  // of all legend items and as such is not helpful.
  const uniqueLabelPrefixes = new Set(
    Array.from(items.keys())
      .map((i) => i.split(" | ")[0])
      .flat(),
  );
  if (uniqueLabelPrefixes.size === 1 && itemEntries.length > 1) {
    // If all items share the same label prefix, use it as the main label
    const mainLabel = Array.from(uniqueLabelPrefixes)[0];
    itemEntries.forEach(([, item]) => {
      item.label = item.label.replace(`${mainLabel} | `, "");
    });
  }

  const displayedItems = itemEntries.slice(0, limit);
  const remainingItems = itemEntries.slice(limit);
  const hasMore = itemEntries.length > limit;

  // For horizontal layout, split items into columns (top to bottom, then left to right)
  const getColumns = (
    items: [string, SeriesConfigItem][],
    numColumns: number = 3,
  ) => {
    const baseItemsPerColumn = Math.floor(items.length / numColumns);
    const remainder = items.length % numColumns;
    const columns: [string, SeriesConfigItem][][] = [];

    let startIndex = 0;
    for (let i = 0; i < numColumns; i++) {
      // First 'remainder' columns get one extra item
      const itemsInThisColumn = baseItemsPerColumn + (i < remainder ? 1 : 0);
      const columnItems = items.slice(
        startIndex,
        startIndex + itemsInThisColumn,
      );
      if (columnItems.length > 0) {
        columns.push(columnItems);
      }
      startIndex += itemsInThisColumn;
    }

    return columns;
  };

  const LegendItem = ({
    itemKey,
    item,
  }: {
    itemKey: string;
    item: SeriesConfigItem;
  }) => {
    const [label, setLabel] = React.useState(item.label || "");
    return (
      <div
        key={itemKey}
        className={`flex items-center gap-1 ${item.hidden ? "opacity-25" : ""}`}
      >
        <ColorSelect.Root
          value={item.color}
          onValueChange={(value) =>
            onItemChange({ key: itemKey, field: "color", newValue: value })
          }
        >
          <ColorSelectTrigger
            className="w-5 h-5 p-0 flex-shrink-0"
            style={{ backgroundColor: item.color }}
          >
            <ColorSelect.Value />
          </ColorSelectTrigger>
          <ColorSelectContent className="w-min p-1">
            <label className="font-medium mb-3 text-xs text-muted-foreground flex flex-nowrap items-center cursor-pointer truncate">
              <input
                type="checkbox"
                checked={item.hidden || false}
                onChange={(e) => {
                  onItemChange({
                    key: itemKey,
                    field: "hidden",
                    newValue: e.target.checked,
                  });
                }}
                className="h-[15px] w-[15px] mt-1 ml-[4px] mr-1 cursor-pointer"
              />
              <div className="mt-2">Hide item</div>
            </label>
            <hr />

            {COLOR_PALETTE.map((color) => (
              <ColorSelectItem
                key={color}
                value={color}
                className="!bg-[var(--option-color)] p-0 h-5 w-5 border border-border"
                style={{ "--option-color": color } as React.CSSProperties}
              >
                <span className="invisible">Color</span>
              </ColorSelectItem>
            ))}
          </ColorSelectContent>
        </ColorSelect.Root>
        <input
          key={itemKey}
          type="text"
          value={label}
          onChange={(e) => {
            setLabel(e.target.value);
          }}
          onBlur={() => {
            onItemChange({
              key: itemKey,
              field: "label",
              newValue: label,
            });
          }}
          className="border-0 bg-transparent text-xs truncate w-full p-0 leading-none min-h-5 h-auto"
          title={item.label}
        />
      </div>
    );
  };

  const MoreItemsPopover = () => (
    <Popover.Root>
      <Popover.Trigger asChild>
        <div className="flex items-center p-1 cursor-pointer text-xs text-muted-foreground hover:text-foreground">
          +{remainingItems.length} more...
        </div>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="bg-popover text-popover-foreground rounded-md shadow-md p-2 max-h-[300px] overflow-y-auto"
          sideOffset={5}
        >
          <div className="flex flex-col">
            {remainingItems.map(([key, item]) => (
              <LegendItem key={key} itemKey={key} item={item} />
            ))}
          </div>
          <Popover.Arrow className="fill-popover" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );

  const renderItems = () => {
    if (displayedItems.length <= 1) return null;

    if (direction === "horizontal") {
      const columns = getColumns(displayedItems, 3);
      return columns.map((column, columnIndex) => (
        <div key={columnIndex} className="flex flex-col gap-1">
          {column.map(([key, item]) => (
            <LegendItem key={key} itemKey={key} item={item} />
          ))}
        </div>
      ));
    }

    return displayedItems.map(([key, item]) => (
      <LegendItem key={key} itemKey={key} item={item} />
    ));
  };

  const containerClassName =
    direction === "horizontal"
      ? "flex flex-row gap-4 justify-center items-start w-full"
      : "flex flex-col max-h-[400px] overflow-y-auto";

  return (
    <div className={containerClassName}>
      {renderItems()}
      {hasMore && <MoreItemsPopover />}
    </div>
  );
};

export default LegendV2;
